export interface Searchable {
    label: string;
    module?: string;
    /** Synonyms/keywords that let an item match queries its label doesn't contain (improves findability). */
    tags?: string[];
    /** Items marked demoted always rank below non-demoted results. */
    demoted?: boolean;
}

function normalize(s: string): string {
    return s.toLowerCase().trim();
}

/** Returns true if all chars of query appear in order inside str (fuzzy sequence). */
function fuzzySequence(str: string, query: string): boolean {
    let qi = 0;
    for (let i = 0; i < str.length && qi < query.length; i++) {
        if (str[i] === query[qi]) qi++;
    }
    return qi === query.length;
}

/** Levenshtein edit distance between two strings (two-row dynamic programming). */
function levenshtein(a: string, b: string): number {
    if (a === b) return 0;
    if (!a.length) return b.length;
    if (!b.length) return a.length;
    let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
    for (let i = 1; i <= a.length; i++) {
        const curr = [i];
        for (let j = 1; j <= b.length; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
        }
        prev = curr;
    }
    return prev[b.length];
}

/** 0..1 closeness of query to label by edit distance — 1 means (near-)identical, 0 means unrelated. */
function closeness(label: string, query: string): number {
    const max = Math.max(label.length, query.length);
    return max === 0 ? 0 : 1 - levenshtein(label, query) / max;
}

// Returns a raw relevance score (higher = better); demotion is applied at sort time, not here.
function scoreItem(item: Searchable, query: string): number {
    const label = normalize(item.label);
    const mod = item.module ? normalize(item.module) : '';
    const tags = item.tags?.map(normalize) ?? [];
    const q = normalize(query);

    if (!q) return 1;

    // Top-level pages (no parent module) get a priority bonus.
    const pageBonus = mod ? 0 : 15;

    // Coherence bonus: this tab belongs to a module that itself matches the query.
    // Ensures "Alle Rechnungen (Rechnungen)" outranks "Rechnungseinstellungen (Trainer Buchungen)"
    // even when the latter has a stronger label match.
    const parentMatches =
        mod && (mod === q || mod.startsWith(q) || mod.split(/\s+/).some((w) => w.startsWith(q)) || mod.includes(q));
    const coherenceBonus = parentMatches ? 20 : 0;

    // Closeness bonus: within a tier, a label the query nearly fills (e.g. "rechnung"→"rechnungen")
    // outranks a long one that merely starts with it (e.g. "rechnungssteller einstellungen").
    // Scaled to 22 so it can offset the coherence bonus when the label match is clearly tighter.
    const closenessBonus = Math.round(closeness(label, q) * 22);

    // Tier 1: exact label match
    if (label === q) return 100 + pageBonus + coherenceBonus + closenessBonus;

    // Tier 2: label starts with query
    if (label.startsWith(q)) return 88 + pageBonus + coherenceBonus + closenessBonus;

    // Tier 3: any word in label starts with query
    if (label.split(/\s+/).some((w) => w.startsWith(q))) return 72 + pageBonus + coherenceBonus + closenessBonus;

    // Tier 4: label contains query as substring
    if (label.includes(q)) return 56 + pageBonus + coherenceBonus + closenessBonus;

    // Tier 4.5: tag match — synonyms/keywords carry an item whose label doesn't match.
    // Scored just below a label substring so genuine label hits still win, but above module-only hits.
    if (tags.length) {
        if (tags.some((t) => t === q || t.startsWith(q))) return 54 + pageBonus + coherenceBonus;
        if (tags.some((t) => t.split(/\s+/).some((w) => w.startsWith(q)))) return 44 + pageBonus + coherenceBonus;
        if (tags.some((t) => t.includes(q))) return 30 + pageBonus + coherenceBonus;
    }

    // Tier 5: multi-word query — every part must match label, module, or a tag
    const parts = q.split(/\s+/).filter(Boolean);
    if (parts.length > 1 && parts.every((p) => label.includes(p) || mod.includes(p) || tags.some((t) => t.includes(p))))
        return 46 + pageBonus + coherenceBonus + closenessBonus;

    // Tier 6: module contains query as substring (keyword/description match)
    if (mod.includes(q)) return 25;

    // Tier 7: fuzzy character sequence on label only
    // Intentionally no fuzzy on module — long descriptions cause too many false positives.
    if (q.length >= 2 && fuzzySequence(label, q)) return 18 + pageBonus + coherenceBonus + closenessBonus;

    return 0;
}

// Scores, filters and orders items; demoted items (e.g. settings sections) always rank below
// non-demoted ones, with relevance score breaking ties within each group.
export function searchItems<T extends Searchable>(items: T[], query: string): T[] {
    if (!query.trim()) return items;
    return items
        .map((item) => ({ item, score: scoreItem(item, query) }))
        .filter(({ score }) => score > 0)
        .sort((a, b) => {
            const demote = Number(a.item.demoted ?? false) - Number(b.item.demoted ?? false);
            return demote !== 0 ? demote : b.score - a.score;
        })
        .map(({ item }) => item);
}
